import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import './index.css';
import { supabase } from './supabaseClient';
import { useLanguage } from './LanguageContext';

// --- KOMPONENTER ---
import InboxView from './components/InboxView';
import SavedView from './components/SavedView';
import WatchesView from './components/WatchesView';
import SummariesView from './components/SummariesView';
import HomeView from './components/HomeView';
import TopicsView from './components/TopicsView';
import LoginView from './components/LoginView';
import Header from './components/header';
import Sidebar from './components/Sidebar';
import PaywallView from './components/PaywallView';
import OfflineScreen from './components/OfflineScreen';
import TermsView from './components/TermsView';
import PrivacyView from './components/PrivacyView';
import { t } from './translations';
import AboutView from './components/AboutView';

// --- UTILS MED KVOTHANTERING ---
import {
  renderAISummary,
  initializeRevenueCat,
  hasReachedSummaryLimit,
  recordSummaryUsage,
  MONTHLY_SUMMARY_LIMIT
} from './utils';

// --- HOOKS ---
import { useOpenAlex } from './hooks/useOpenAlex';
import { useSupabaseData } from './hooks/useSupabaseData';
import { useUsageCheck } from './hooks/useUsageCheck';

// --- GLOBALA KONSTANTER ---
const ADMIN_EMAIL = 'fredrik.mokvist@gmail.com';
const TRIAL_SUMMARY_LIMIT = 20;

// --- Animations-inställningar för sidövergångar ---
const pageVariants = {
  initial: { opacity: 0, x: 20 },
  in: { opacity: 1, x: 0 },
  out: { opacity: 0, x: -20 }
};

const pageTransition = { type: 'tween', ease: 'anticipate', duration: 0.3 };

const ResearchSummaryApp = () => {
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const isAdmin = session?.user?.email === ADMIN_EMAIL;

  const navigate = useNavigate();
  const location = useLocation();

  const view = location.pathname.replace('/', '') || 'home';

  const {
    authorQuery, setAuthorQuery, authorSuggestions, selectedAuthor, setSelectedAuthor,
    isSearchingAuthors, searchForAuthor, selectAuthor,
    journalQuery, setJournalQuery, journalSuggestions, selectedJournal, setSelectedJournal,
    isSearchingJournals, searchForJournal, selectJournal,
    institutionQuery, setInstitutionQuery, institutionSuggestions, selectedInstitution, setSelectedInstitution,
    isSearchingInstitutions, searchForInstitution, selectInstitution,
    departmentQuery, setDepartmentQuery
  } = useOpenAlex();

  const {
    savedWatches, fetchWatches, deleteWatch,
    savedArticles, fetchSavedArticles, deleteSavedArticle,
    inboxMatches, fetchInbox
  } = useSupabaseData(session);

  const { language } = useLanguage();

  const { validateBeforeGeneration, incrementUsage } = useUsageCheck(session?.user?.id);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getToday = () => new Date().toISOString().split('T')[0];
  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  };

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customTopic, setCustomTopic] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('allmänhet');
  const [fromDate, setFromDate] = useState('2010-01-01');
  const [toDate, setToDate] = useState(getToday());

  const [loading, setLoading] = useState(false);
  const [papers, setPapers] = useState([]);
  const [error, setError] = useState(null);
  const [forcePaywall, setForcePaywall] = useState(false);

  const [blockedItems, setBlockedItems] = useState([]);

  // --- HÄMTA ANVÄNDARPROFIL ---
  const fetchUserProfile = async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error("Fel vid hämtning av användarprofil:", error.message);
    } else if (data) {
      setUserProfile(data);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
        initializeRevenueCat(session.user.id, session.user.email);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
        initializeRevenueCat(session.user.id, session.user.email);
      } else {
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- STRIPE CHECKOUT RETURN POLLING (?payment=success) ---
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus === 'success' && session?.user?.id) {
      let attempts = 0;
      const maxAttempts = 6;

      const pollInterval = setInterval(async () => {
        attempts += 1;

        const { data: updatedProfile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (error) {
          console.error("Fel vid polling av profil:", error.message);
        } else if (updatedProfile) {
          const isNowPaid =
            updatedProfile.status === 'basic' ||
            updatedProfile.status === 'premium' ||
            updatedProfile.subscription_tier === 'basic' ||
            updatedProfile.subscription_tier === 'premium';

          if (isNowPaid || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setUserProfile(updatedProfile);
            setForcePaywall(false);
            navigate('/', { replace: true });
          }
        }
      }, 1000);

      return () => clearInterval(pollInterval);
    } else if (paymentStatus === 'cancelled') {
      navigate('/', { replace: true });
    }
  }, [location.search, session?.user?.id, navigate]);

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
    if (error) console.error("Inloggningsfel:", error.message);
  };

  const handleAppleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin }
    });
    if (error) console.error("Apple login error:", error.message);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const validInboxMatches = (inboxMatches || []).filter(item => {
    if (item.is_deleted) return false;
    if (blockedItems.includes(String(item.id))) return false;
    if (item.article_title && blockedItems.includes(item.article_title)) return false;
    if (item.title && blockedItems.includes(item.title)) return false;
    return true;
  });

  const dismissedInboxItems = validInboxMatches.filter(item => item.is_read).map(item => item.id);

  const getGroupedInbox = () => {
    if (!validInboxMatches) return {};
    const testGroupTitle = t(language, 'inbox_group_test_runs') || '🧪 Manuella testsökningar';
    const fallbackTitle = t(language, 'inbox_group_other');

    const grouped = validInboxMatches.reduce((acc, item) => {
      const title = item.watches?.title 
        ? item.watches.title 
        : (item.watch_id ? fallbackTitle : testGroupTitle);

      if (!acc[title]) acc[title] = [];
      acc[title].push(item);
      return acc;
    }, {});
    return grouped;
  };

  const isPaidUser =
    userProfile?.status === 'basic' ||
    userProfile?.status === 'premium' ||
    userProfile?.subscription_tier === 'basic' ||
    userProfile?.subscription_tier === 'premium';

  const isTrialFinished = () => {
    if (!userProfile || isPaidUser) return false;
    const summariesUsed = userProfile.summaries_generated_this_month || 0;
    if (summariesUsed >= TRIAL_SUMMARY_LIMIT) return true;

    if (userProfile.created_at) {
      const createdDate = new Date(userProfile.created_at);
      const diffDays = Math.ceil(Math.abs(new Date() - createdDate) / (1000 * 60 * 60 * 24));
      if (diffDays > 14) return true;
    }
    return false;
  };

  const isBasicQuotaExceeded = !isAdmin && isPaidUser && hasReachedSummaryLimit(userProfile, MONTHLY_SUMMARY_LIMIT);

  // Inkorgen hålls läsbar som ett arkiv och blockeras inte vid nådd provkvot
  const shouldBlockInbox = false;

  // Kontrollera om provkvoten är slut eller provperioden avslutad för att visa bannern
  const isTrialPaused = !isAdmin && !isPaidUser && isTrialFinished();

  const handleNavClick = (newView) => {
    if (newView === 'inbox' && shouldBlockInbox) {
      setForcePaywall(true);
      setIsMobileMenuOpen(false);
      navigate('/inbox');
      return;
    }

    setForcePaywall(false);

    if (newView === 'home') navigate('/');
    else navigate(`/${newView}`);
    setIsMobileMenuOpen(false);
  };

  const deleteItem = async (rawId) => {
    const id = typeof rawId === 'object' ? rawId.id : rawId;
    const targetItem = inboxMatches?.find(i => String(i.id) === String(id));

    let newBlocks = [String(id)];
    const title = targetItem?.article_title || targetItem?.title;
    if (title) {
      newBlocks.push(title);
    }
    setBlockedItems(prev => [...prev, ...newBlocks]);

    const { error } = await supabase
      .from('daily_matches')
      .update({ is_deleted: true })
      .eq('id', id);

    if (error) {
      console.error("Fel vid döljande av artikel:", error.message);
    }
  };

  const markAsRead = async (id) => {
    const { error } = await supabase
      .from('daily_matches')
      .update({ is_read: true })
      .eq('id', id);

    if (error) {
      console.error("Fel vid uppdatering av läst-status:", error);
    } else {
      fetchInbox();
    }
  };

  const generateAISummary = async (abstract, title, targetLanguage) => {
    try {
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !currentSession) {
        throw new Error(t(language, 'error_auth_required'));
      }

      const apiUrl = 'https://science-monitor.vercel.app/api/generate-summary';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`
        },
        body: JSON.stringify({ abstract, title, targetLanguage, userId: currentSession.user?.id })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const serverMessage = errorData.error || 'Inget detaljerat felmeddelande från servern';
        throw new Error(serverMessage);
      }

      const data = await response.json();
      return data.summary;
    } catch (err) {
      console.error("Fel i generateAISummary:", err);
      throw err;
    }
  };

  const reconstructAbstract = (invertedIndex) => {
    if (!invertedIndex) return null;
    const sorted = Object.entries(invertedIndex)
      .flatMap(([word, positions]) => positions.map(pos => ({ word, pos })))
      .sort((a, b) => a.pos - b.pos);
    return sorted.map(item => item.word).join(' ');
  };

  // --- SPARA BEVAKNING ---
  const saveWatch = async () => {
    const rawStatus = userProfile?.status || userProfile?.subscription_tier;
    const status = (rawStatus === 'basic' || rawStatus === 'premium') ? rawStatus : 'trial';

    const tierLimits = {
      trial: { maxWatches: 1, maxJournals: 1 },
      basic: { maxWatches: 5, maxJournals: 1 },
      premium: { maxWatches: 20, maxJournals: 2 }
    };

    const limits = tierLimits[status] || tierLimits.trial;

    if (!isAdmin) {
      const { data: currentWatches, error: fetchError } = await supabase
        .from('watches')
        .select('journal_data')
        .eq('user_id', session.user.id);

      if (fetchError) {
        alert(t(language, 'save_watch_fetch_error'));
        return;
      }

      const totalWatches = currentWatches?.length || 0;
      const totalJournals = (currentWatches || []).filter(w => w.journal_data !== null).length;

      if (totalWatches >= limits.maxWatches) {
        if (status === 'trial') {
          setForcePaywall(true);
        } else {
          const limitMessage = t(language, 'save_watch_limit_reached')
            .replace('{tier}', status.toUpperCase())
            .replace('{max}', limits.maxWatches);
          alert(limitMessage);
        }
        return;
      }

      if (selectedJournal && totalJournals >= limits.maxJournals) {
        const journalLimitMessage = t(language, 'save_watch_journal_limit_reached')
          .replace('{tier}', status.toUpperCase())
          .replace('{max}', limits.maxJournals);
        alert(journalLimitMessage);
        return;
      }
    }

    const title = generateWatchTitle();

    const finalInstitutionData = selectedInstitution ? {
      ...selectedInstitution,
      department: departmentQuery?.trim() || null
    } : null;

    const { error } = await supabase
      .from('watches')
      .insert([{
        title: title,
        author_data: selectedAuthor,
        journal_data: selectedJournal,
        institution_data: finalInstitutionData,
        topics: selectedTopics,
        custom_query: customTopic,
        country: selectedCountries.length > 0 ? selectedCountries.join(',') : null,
        user_id: session.user.id,
        language: language,
        level: selectedLevel,
      }]);

    if (error) {
      alert(t(language, 'save_watch_error'));
      console.error(error);
    } else {
      alert(t(language, 'save_watch_success'));
      fetchWatches();
    }
  };

  const loadWatch = (watch) => {
    setFromDate(getYesterday());
    setToDate(getToday());

    setSelectedAuthor(watch.author_data);
    setAuthorQuery(watch.author_data ? watch.author_data.name : '');
    setSelectedJournal(watch.journal_data);
    setJournalQuery(watch.journal_data ? watch.journal_data.name : '');

    setSelectedInstitution(watch.institution_data || null);
    setInstitutionQuery(watch.institution_data ? watch.institution_data.name : '');
    setDepartmentQuery(watch.institution_data?.department || '');

    setSelectedTopics(watch.topics || []);
    setCustomTopic(watch.custom_query || '');

    setSelectedCountries(watch.country ? watch.country.split(',') : []);
    setSelectedLevel(watch.level || 'allmänhet');
    handleNavClick('topics');
  };

  const generateWatchTitle = () => {
    let parts = [];
    if (selectedAuthor) parts.push(selectedAuthor.name);
    if (selectedJournal) parts.push(selectedJournal.name);
    if (selectedInstitution) {
      const instTitle = departmentQuery?.trim()
        ? `${selectedInstitution.name} (${departmentQuery.trim()})`
        : selectedInstitution.name;
      parts.push(instTitle);
    }
    if (selectedCountries.length > 0) parts.push(`${t(language, 'watch_title_countries')}${selectedCountries.join(', ')}`);
    if (selectedTopics.length > 0) parts.push(selectedTopics.map(t => t.name).join(', '));
    if (customTopic) parts.push(customTopic);
    return parts.join(' + ') || t(language, 'watch_title_fallback');
  };

  const fetchOpenAlexPapers = async (topicQuery = null) => {
    try {
      let filters = [`from_publication_date:${fromDate}`, `to_publication_date:${toDate}`];

      if (selectedAuthor) filters.push(`author.id:${selectedAuthor.id}`);
      else if (authorQuery.trim()) filters.push(`author.display_name.search:${encodeURIComponent(authorQuery.trim())}`);

      if (selectedJournal) filters.push(`primary_location.source.id:${selectedJournal.id}`);
      else if (journalQuery.trim()) filters.push(`primary_location.source.display_name.search:${encodeURIComponent(journalQuery.trim())}`);

      if (selectedInstitution) {
        filters.push(`institutions.id:${selectedInstitution.id}`);
      } else if (institutionQuery.trim()) {
        filters.push(`institutions.display_name.search:${encodeURIComponent(institutionQuery.trim())}`);
      }

      if (selectedCountries.length > 0) filters.push(`institutions.country_code:${selectedCountries.join('|')}`);

      let url = `https://api.openalex.org/works?filter=${filters.join(',')}`;

      let searchParts = [];
      if (topicQuery) searchParts.push(topicQuery);
      if (departmentQuery && departmentQuery.trim()) searchParts.push(departmentQuery.trim());

      if (searchParts.length > 0) {
        url += `&search=${encodeURIComponent(searchParts.join(' '))}`;
      }

      const sortParam = 'cited_by_count:desc';
      url += `&per_page=15&sort=${sortParam}&mailto=fredrik.mokvist@gmail.com`;

      const response = await fetch(url);
      const data = await response.json();

      if (!data.results) return [];

      const validResults = data.results.filter(work => work.abstract_inverted_index !== null && work.abstract_inverted_index !== undefined);

      return validResults.map(work => ({
        id: work.id,
        title: work.title,
        abstract: reconstructAbstract(work.abstract_inverted_index),
        authors: work.authorships?.length > 3
          ? `${work.authorships.slice(0, 3).map(a => a.author.display_name).join(', ')} et al.`
          : work.authorships?.map(a => a.author.display_name).join(', ') || t(language, 'inbox_unknown_author'),
        date: work.publication_date,
        link: work.doi || work.id,
        topic: topicQuery || (selectedAuthor ? `${t(language, 'watch_author_prefix')}${selectedAuthor.name}` : (selectedInstitution ? selectedInstitution.name : t(language, 'watch_search_default'))),
        venue: work.primary_location?.source?.display_name || t(language, 'inbox_unknown_journal'),
        aiSummary: null
      }));
    } catch (e) { return []; }
  };

  const consumeSummary = async () => {
    if (isAdmin) return true;

    if (isTrialFinished() || isBasicQuotaExceeded) {
      console.warn("🛑 Användaren har nått sitt tak för sammanfattningar.");
      setForcePaywall(true);
      return false;
    }

    if (session?.user?.id) {
      const result = await recordSummaryUsage(session.user.id);
      if (result.success && result.count !== null) {
        setUserProfile(prev => ({
          ...prev,
          summaries_generated_this_month: result.count
        }));
      }
    }

    return true;
  };

  const performGeneration = async () => {
    const hasAuthor = selectedAuthor || authorQuery.trim() !== '';
    const hasJournal = selectedJournal || journalQuery.trim() !== '';
    const hasInstitution = selectedInstitution || institutionQuery.trim() !== '';
    const hasTopics = selectedTopics.length > 0;
    const hasCustom = customTopic.trim() !== '';
    const hasCountry = selectedCountries.length > 0;

    if (!hasAuthor && !hasJournal && !hasInstitution && !hasTopics && !hasCustom && !hasCountry) {
      setError(t(language, 'error_select_criteria'));
      return;
    }

    if (!isAdmin) {
      if (isTrialFinished() || isBasicQuotaExceeded) {
        setForcePaywall(true);
        return;
      }

      try {
        const isAllowed = await validateBeforeGeneration('testRun');
        if (!isAllowed) {
          setForcePaywall(true);
          return;
        }
      } catch (valErr) {
        console.warn("Could not validate before generation:", valErr);
      }
    }

    setLoading(true);
    setPapers([]);
    setError(null);

    try {
      let allFound = [];
      const searchTerms = [...selectedTopics.map(t => t.query)];
      if (hasCustom) searchTerms.push(customTopic.trim());

      if (searchTerms.length > 0) {
        for (const term of searchTerms) {
          const res = await fetchOpenAlexPapers(term);
          allFound.push(...res);
        }
      } else {
        const res = await fetchOpenAlexPapers(null);
        allFound.push(...res);
      }

      const uniquePapers = Array.from(new Map(allFound.map(p => [p.id, p])).values());

      if (uniquePapers.length === 0) {
        setError(t(language, 'error_no_abstracts'));
        setLoading(false);
        return;
      }

      const limitedPapers = uniquePapers.slice(0, 1);
      setPapers(limitedPapers);

      for (const paper of limitedPapers) {
        const summary = await generateAISummary(paper.abstract, paper.title, language);
        setPapers(current => current.map(p => p.id === paper.id ? { ...p, aiSummary: summary } : p));

        if (session?.user?.id) {
          try {
            const { error: insertError } = await supabase.from('daily_matches').insert([{
              user_id: session.user.id,
              watch_id: null,
              article_id: paper.id,
              article_title: paper.title,
              authors: paper.authors,
              venue: paper.venue,
              article_date: paper.date,
              article_url: paper.link,
              ai_summary: summary,
              level: selectedLevel || 'allmänhet'
            }]);

            if (insertError) {
              console.error("Fel vid sparande av provsökning i Supabase:", insertError);
            } else {
              await fetchInbox();
            }
          } catch (dbErr) {
            console.warn("Kunde inte spara provsökning till inkorgen:", dbErr);
          }
        }

        if (!isAdmin && session?.user?.id) {
          const usageResult = await recordSummaryUsage(session.user.id);
          if (usageResult.success && usageResult.count !== null) {
            setUserProfile(prev => ({
              ...prev,
              summaries_generated_this_month: usageResult.count
            }));
          }
        }
      }

      if (!isAdmin) {
        await incrementUsage('testRun');
      }

      handleNavClick('inbox');

    } catch (err) {
      console.error("Fel vid generering:", err);

      const errorMessage = err.message || String(err);

      if (errorMessage.includes("Limit reached") || errorMessage.includes("Maximum 5")) {
        setForcePaywall(true);
        setPapers([]);
      } else {
        setError(t(language, 'error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => prev.find(t => t.name === topic.name) ? prev.filter(t => t.name !== topic.name) : [...prev, topic]);
  };

  const addCustomTopic = () => {
    if (customTopic.trim() && !selectedTopics.find(t => t.name === customTopic.trim())) {
      setSelectedTopics(prev => [...prev, { name: customTopic.trim(), query: customTopic.trim(), icon: '🔍' }]);
      setCustomTopic('');
    }
  };

  const saveArticle = async (paper) => {
    const { error } = await supabase
      .from('saved_articles')
      .insert([{
        user_id: session.user.id,
        article_id: paper.id,
        article_title: paper.title,
        authors: paper.authors,
        venue: paper.venue,
        article_date: paper.date,
        article_url: paper.link,
        doi: paper.link,
        ai_summary: paper.aiSummary || t(language, 'summary_no_text'),
        topic: paper.topic
      }]);

    if (error) {
      console.error('Fel vid sparning:', error);
      alert(t(language, 'error_save_article_failed'));
    } else {
      setPapers(current => current.map(p => p.id === paper.id ? { ...p, isSaved: true } : p));
      fetchSavedArticles();
    }
  };

  const saveFromInbox = async (item) => {
    const { error } = await supabase
      .from('saved_articles')
      .insert([{
        user_id: session.user.id,
        article_id: item.article_id || item.id || `inbox-${Date.now()}`,
        article_title: item.article_title || item.title || t(language, 'saved_unknown_title'),
        authors: item.authors || item.author || t(language, 'inbox_unknown_author'),
        venue: item.venue || item.journal || t(language, 'inbox_unknown_journal'),
        article_date: item.article_date || item.date || item.published_date || new Date().toISOString().split('T')[0],
        article_url: item.article_url || item.url || item.link || item.doi || '',
        doi: item.doi || item.url || item.link || '',
        ai_summary: item.ai_summary || item.summary || item.abstract || t(language, 'summary_no_text'),
        topic: item.watches?.title || item.topic || t(language, 'saved_from_inbox_topic')
      }]);

    if (error) {
      console.error('Fel vid arkivering:', error);
      alert(`${t(language, 'error_archive_failed')}${error.message}`);
      return;
    }

    await deleteItem(item.id);
    fetchSavedArticles();
  };

  const isPublicRoute = location.pathname === '/terms' || location.pathname === '/privacy' || location.pathname === '/about';

  if (!session && !isPublicRoute) {
    return (
      <LoginView
        handleGoogleLogin={handleGoogleLogin}
        handleAppleLogin={handleAppleLogin}
      />
    );
  }

  const handlePurchaseSuccess = async () => {
    setForcePaywall(false);

    if (session?.user?.id) {
      try {
        await fetchUserProfile(session.user.id);
      } catch (err) {
        console.error("Oväntat fel vid profiluppdatering efter köp:", err);
      }
    }
  };

  // --- ÅTERANSLUTNING VID OFFLINELÄGE ---
  const handleReconnect = async () => {
    if (session?.user?.id) {
      try {
        await Promise.all([
          fetchUserProfile(session.user.id),
          fetchInbox(),
          fetchSavedArticles(),
          fetchWatches()
        ]);
      } catch (syncErr) {
        console.warn("Kunde inte slutföra omsynkning efter offline:", syncErr);
      }
    }
  };

  const groupedInbox = getGroupedInbox();

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 flex font-sans w-full max-w-full overflow-x-hidden relative">

      {session && (
        <Sidebar
          view={view}
          handleNavClick={handleNavClick}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          inboxMatches={validInboxMatches}
          dismissedInboxItems={dismissedInboxItems}
          fetchSavedArticles={fetchSavedArticles}
          handleLogout={handleLogout}
          session={session}
          userProfile={userProfile}
        />
      )}

      <OfflineScreen onReconnect={handleReconnect} />

      <div className={`flex-1 ${session ? 'md:ml-64 pt-16 pb-24 md:pt-0 md:pb-0' : 'p-0'} flex flex-col min-h-screen w-full max-w-full overflow-x-hidden relative box-border`}>

        <div className="absolute inset-0 z-0 opacity-20 pointer-events-none overflow-hidden" aria-hidden="true">
          <svg className="absolute -top-10 -left-16 w-96 h-96 text-slate-300" viewBox="0 0 100 100" fill="currentColor">
            <path d="M30,20 L50,10 L70,20 L70,40 L50,50 L30,40 Z" fill="none" stroke="currentColor" strokeWidth="1"/>
            <circle cx="30" cy="20" r="3"/> <circle cx="50" cy="10" r="3"/> <circle cx="70" cy="20" r="3"/>
            <circle cx="70" cy="40" r="3"/> <circle cx="50" cy="50" r="3"/> <circle cx="30" cy="40" r="3"/>
            <path d="M50,50 L50,70 L70,80 L90,70 L90,50 L70,40" fill="none" stroke="currentColor" strokeWidth="1"/>
            <circle cx="50" cy="70" r="3"/> <circle cx="70" cy="80" r="3"/> <circle cx="90" cy="70" r="3"/> <circle cx="90" cy="50" r="3"/>
          </svg>

          <svg className="absolute top-[35%] -right-20 w-80 h-80 text-slate-300" viewBox="0 0 100 100" fill="currentColor">
            <path d="M10,40 L30,30 L50,40 L50,60 L30,70 L10,60 Z" fill="none" stroke="currentColor" strokeWidth="1"/>
            <circle cx="10" cy="40" r="3"/> <circle cx="30" cy="30" r="3"/> <circle cx="50" cy="40" r="3"/>
            <circle cx="50" cy="60" r="3"/> <circle cx="30" cy="70" r="3"/> <circle cx="10" cy="60" r="3"/>
            <path d="M50,40 L70,30 L90,40 L90,60 L70,70 L50,60" fill="none" stroke="currentColor" strokeWidth="1"/>
            <circle cx="70" cy="30" r="3"/> <circle cx="90" cy="40" r="3"/> <circle cx="90" cy="60" r="3"/> <circle cx="70" cy="70" r="3"/>
          </svg>

          <svg className="absolute top-[75%] -left-10 w-64 h-64 text-slate-300" viewBox="0 0 100 100" fill="currentColor">
            <circle cx="50" cy="50" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            <line x1="50" y1="40" x2="50" y2="10" stroke="currentColor" strokeWidth="1.5"/> <circle cx="50" cy="10" r="4"/>
            <line x1="60" y1="50" x2="90" y2="50" stroke="currentColor" strokeWidth="1.5"/> <circle cx="90" cy="50" r="4"/>
            <line x1="50" y1="60" x2="50" y2="90" stroke="currentColor" strokeWidth="1.5"/> <circle cx="50" cy="90" r="4"/>
            <line x1="40" y1="50" x2="10" y2="50" stroke="currentColor" strokeWidth="1.5"/> <circle cx="10" cy="50" r="4"/>
          </svg>
        </div>

        {session && <Header view={view} handleLogout={handleLogout} userProfile={userProfile} />}

        <main className={`max-w-5xl mx-auto w-full ${session ? 'p-4 md:p-10' : 'p-0'} flex-1 box-border relative z-10`}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>

              <Route 
                path="/terms" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <TermsView />
                  </motion.div>
                } 
              />

              <Route 
                path="/privacy" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <PrivacyView />
                  </motion.div>
                } 
              />
              <Route 
                path="/about" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <AboutView />
                  </motion.div>
                } 
              />

              <Route 
                path="/" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <HomeView session={session} setView={handleNavClick} watches={savedWatches} savedArticles={savedArticles} inboxMatches={validInboxMatches} />
                  </motion.div>
                } 
              />

              <Route 
                path="/inbox" 
                element={
                  forcePaywall ? (
                    <PaywallView
                      onPurchaseSuccess={handlePurchaseSuccess}
                      isQuotaExceeded={isBasicQuotaExceeded}
                      onClose={() => {
                        setForcePaywall(false);
                        navigate('/');
                      }}
                    />
                  ) : (
                    <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                      {/* ⏸️ PAUS-BANNER OM PROVKVOTEN ÄR NÅDD */}
                      {isTrialPaused && (
                        <div className="mb-6 p-4 md:p-5 rounded-2xl bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800/60 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 mt-0.5 shrink-0">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                                Dina bevakningar är pausade ({userProfile?.summaries_generated_this_month || TRIAL_SUMMARY_LIMIT}/{TRIAL_SUMMARY_LIMIT} sammanfattningar)
                              </h4>
                              <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">
                                Du kan fortsätta läsa dina sammanfattningar nedan. Uppgradera till Basic för att fortsätta ta emot nya sammanfattningar varje natt.
                              </p>
                            </div>
                          </div>

                          <button
                            onClick={() => setForcePaywall(true)}
                            className="whitespace-nowrap px-4 py-2 text-xs font-semibold rounded-xl bg-amber-600 hover:bg-amber-700 active:scale-95 text-white shadow-sm transition-all duration-150 flex items-center gap-1.5 self-end sm:self-center shrink-0 cursor-pointer"
                          >
                            <span>Återaktivera med Basic (25 kr/mån)</span>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      )}

                      <InboxView
                        groupedInbox={groupedInbox}
                        fetchInbox={fetchInbox}
                        markAsRead={markAsRead}
                        deleteItem={deleteItem}
                        renderAISummary={renderAISummary}
                        setView={handleNavClick}
                        saveFromInbox={saveFromInbox}
                        userId={session?.user?.id}
                        userProfile={userProfile}
                        consumeSummary={consumeSummary}
                      />
                    </motion.div>
                  )
                } 
              />

              <Route 
                path="/saved" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <SavedView
                      savedArticles={savedArticles}
                      fetchSavedArticles={fetchSavedArticles}
                      deleteSavedArticle={deleteSavedArticle}
                      renderAISummary={renderAISummary}
                    />
                  </motion.div>
                } 
              />

              <Route 
                path="/watches" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <WatchesView 
                      savedWatches={savedWatches} 
                      handleNavClick={handleNavClick} 
                      loadWatch={loadWatch} 
                      deleteWatch={deleteWatch}
                      isTrialPaused={isTrialPaused}
                      onOpenPaywall={() => setForcePaywall(true)}
                      userProfile={userProfile}
                    />
                  </motion.div>
                } 
              />

              <Route 
                path="/topics" 
                element={
                  forcePaywall ? (
                    <PaywallView
                      onPurchaseSuccess={handlePurchaseSuccess}
                      isQuotaExceeded={isBasicQuotaExceeded}
                      onClose={() => {
                        setForcePaywall(false);
                        navigate('/');
                      }}
                    />
                  ) : (
                    <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                      <TopicsView
                        fromDate={fromDate} setFromDate={setFromDate} toDate={toDate} setToDate={setToDate}
                        authorQuery={authorQuery} setAuthorQuery={setAuthorQuery} selectedAuthor={selectedAuthor} setSelectedAuthor={setSelectedAuthor} searchForAuthor={searchForAuthor} isSearchingAuthors={isSearchingAuthors} authorSuggestions={authorSuggestions} selectAuthor={selectAuthor}
                        journalQuery={journalQuery} setJournalQuery={setJournalQuery} selectedJournal={selectedJournal} setSelectedJournal={setSelectedJournal} searchForJournal={searchForJournal} isSearchingJournals={isSearchingJournals} journalSuggestions={journalSuggestions} selectJournal={selectJournal}
                        institutionQuery={institutionQuery} setInstitutionQuery={setInstitutionQuery} selectedInstitution={selectedInstitution} setSelectedInstitution={setSelectedInstitution} searchForInstitution={searchForInstitution} isSearchingInstitutions={isSearchingInstitutions} institutionSuggestions={institutionSuggestions} selectInstitution={selectInstitution}
                        departmentQuery={departmentQuery} setDepartmentQuery={setDepartmentQuery}
                        selectedTopics={selectedTopics} toggleTopic={toggleTopic} customTopic={customTopic} setCustomTopic={setCustomTopic} addCustomTopic={addCustomTopic}
                        selectedCountries={selectedCountries} setSelectedCountries={setSelectedCountries}
                        performGeneration={performGeneration} saveWatch={saveWatch}
                        selectedLevel={selectedLevel} setSelectedLevel={setSelectedLevel}
                        isTrialPaused={isTrialPaused}
                        onOpenPaywall={() => setForcePaywall(true)}
                      />
                    </motion.div>
                  )
                } 
              />

              <Route 
                path="/summaries" 
                element={
                  <motion.div className="w-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
                    <SummariesView
                      loading={loading}
                      papers={papers}
                      error={error}
                      renderAISummary={renderAISummary}
                      saveArticle={saveArticle}
                    />
                  </motion.div>
                } 
              />

            </Routes>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ResearchSummaryApp;
